import { Component, Input, OnInit } from '@angular/core';
import { IVehicleAddress } from '../../interfaces/task-info';
import { SelectItem } from 'primeng';
import { computed, observable } from 'mobx-angular';
import {
  ITaskSupplierScheduleStoreTask,
  TaskSupplierScheduleSource,
  TaskSupplierScheduleStepStore,
  TaskSupplierScheduleStore,
} from './task-supplier-schedule-store';
import { addHours } from 'date-fns';
import { SettingsService, SupplierJobInteractionService } from '../../services';

@Component({
  selector: 'task-supplier-schedule-step',
  templateUrl: './task-supplier-schedule-step.component.html',
  styleUrls: ['./task-supplier-schedule-step.component.scss'],
})
export class TaskSupplierScheduleStepComponent implements OnInit {
  @Input() @observable supplierScheduleStore: TaskSupplierScheduleStore;

  @computed
  public get taskSupplierScheduleStore(): TaskSupplierScheduleStepStore {
    return this.isDropOff ? this.supplierScheduleStore.dropOff : this.supplierScheduleStore.pickUp;
  }

  @Input() task: ITaskSupplierScheduleStoreTask = null;

  @Input() isDropOff: Boolean = false;

  @Input() @observable source: TaskSupplierScheduleSource =
    TaskSupplierScheduleSource.JobDeployment;

  public minDate: Date;
  public minDateDay: Date;
  public maxDate: Date;

  constructor(
    private settingsService: SettingsService,
    private supplierJobInteractionService: SupplierJobInteractionService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.dateOptionsSetup().then(() => {
      this.setDropOffDate();
      this.supplierScheduleStore.pickupScheduledTimeChanged.subscribe(() => {
        this.dateOptionsSetup();
      });
    });
  }

  get isMinAndMaxDateSameDay(): boolean {
    const earliestPickup = new Date(this.task.pickupEarliestDateTime);
    const latestPickup = new Date(this.task.pickupLatestDateTime);
    const earliestDropoff = new Date(this.task.dropoffEarliestDateTime);
    const latestDropoff = new Date(this.task.dropoffLatestDateTime);
    const allDatesAreEqual =
      earliestPickup.getDate() === latestPickup.getDate() &&
      earliestPickup.getDate() === earliestDropoff.getDate() &&
      earliestPickup.getDate() === latestDropoff.getDate();
    return Date.now() < new Date(this.task.dropoffLatestDateTime).getTime()
      ? allDatesAreEqual
      : false;
  }

  async dateOptionsSetup() {
    const settings = await this.settingsService.GetSystemSettings();
    if (this.isDropOff) {
      this.minDate = addHours(this.supplierScheduleStore.pickUp.scheduledTime, 1);
      const dt = new Date(this.minDate);
      dt.setHours(0);
      this.minDateDay = dt;
      this.maxDate = new Date(this.task.dropoffLatestDateTime);
      /*addHours(
        new Date(this.task.dropoffLatestDateTime),
        settings.interactiveScheduleMaxDropoffAdjustment,
      );*/
    } else {
      this.minDate = new Date(this.task.pickupEarliestDateTime);
      this.maxDate = new Date(this.task.pickupLatestDateTime);
      /*
      addHours(
        new Date(this.task.pickupLatestDateTime),
        settings.interactiveScheduleMaxPickupAdjustment,
      );*/
    }
    this.buildProcessedHourBandOptions();
  }

  @computed
  public get title() {
    return this.taskSupplierScheduleStore.isDropOff ? 'Drop Off' : 'Pick Up';
  }

  @computed
  public get locationQualifier() {
    return this.taskSupplierScheduleStore.isDropOff ? 'drop-off' : 'pick-up';
  }

  @computed
  public get subject() {
    switch (this.source) {
      case TaskSupplierScheduleSource.JobDeployment:
      case TaskSupplierScheduleSource.DriverAllocation:
        return 'the driver';
      case TaskSupplierScheduleSource.GrabJob:
        return 'you';
    }
  }

  @computed
  public get scheduledDayQuestionText() {
    return `On which day will ${this.subject} ${this.locationQualifier} the vehicle?`;
  }

  @computed
  public get scheduledHourQuestionText() {
    return `At what time will ${this.subject} ${this.locationQualifier} the vehicle?`;
  }

  public processedHourBandOptions: SelectItem[] = [];

  public buildProcessedHourBandOptions() {
    this.processedHourBandOptions = this.taskSupplierScheduleStore.timeBandSelectOptions
      .map((o) => {
        let date = new Date(this.taskSupplierScheduleStore.scheduledTime);
        date.setHours(o.value as number);
        return {
          ...o,
          disabled: this.toDisableDate(date),
        };
      })
      .filter((o) => !o.disabled);
  }

  private toDisableDate(dateToCompare: Date): boolean {
    if (Date.now() < new Date(this.task.dropoffLatestDateTime).getTime()) {
      return dateToCompare > this.maxDate || dateToCompare < this.minDate;
    } else {
      return false;
      /* This results in an empty list for jobs that were for yesteryear
      /* It is unsafe to say the dropoff date is the same as the pickupdate due to carryovers
      return (
        dateToCompare > this.taskSupplierScheduleStore.maxPastDate ||
        dateToCompare < this.taskSupplierScheduleStore.minPastDate
      );
       */
    }
  }

  @computed
  public get location(): IVehicleAddress {
    return this.taskSupplierScheduleStore.isDropOff
      ? this.task.dropoffLocation
      : this.task.pickupLocation;
  }

  public onScheduledHourChange(value: number) {
    this.supplierJobInteractionService.logSingleJobInteraction(
      'TravelSchedulerInteraction',
      this.task.jobId,
    );
    let date = new Date(this.taskSupplierScheduleStore.scheduledTime);
    date.setHours(value);
    this.taskSupplierScheduleStore.scheduledTime = date;
    this.setDropOffDate();
  }

  public onScheduledDayChange(value: Date) {
    this.supplierJobInteractionService.logSingleJobInteraction(
      'TravelSchedulerInteraction',
      this.task.jobId,
    );
    // Maintain currently selected hour
    value.setHours(this.taskSupplierScheduleStore.scheduledTimeHour);

    this.taskSupplierScheduleStore.scheduledTime = value;
    this.buildProcessedHourBandOptions();
    this.setDropOffDate();
  }

  public onNoneSameDayDeliveryReasonChange(value: number) {
    this.supplierScheduleStore.noneSameDayDeliveryReasonId = value;
  }

  disableDates(task: ITaskSupplierScheduleStoreTask, isDropoff: boolean) {
    let fromDate: Date;
    let toDate: Date;

    if (isDropoff) {
      fromDate = new Date(task.dropoffEarliestDateTime);
      toDate = new Date(task.dropoffLatestDateTime);
    } else {
      fromDate = new Date(task.pickupEarliestDateTime);
      toDate = new Date(task.pickupLatestDateTime);
    }

    // Return if both dates are the same
    return fromDate.getDate() === toDate.getDate();
  }

  setDropOffDate() {
    if (this.isDropOff) {
      return;
    }

    this.supplierScheduleStore.dropOff.scheduledTime =
      this.supplierScheduleStore.scheduledPickupPlusJourneyTime;
    if (
      this.supplierScheduleStore.dropOff.maxPastDate <
      this.supplierScheduleStore.dropOff.scheduledTime
    ) {
      this.supplierScheduleStore.dropOff.updateMaxPastDate();
    }
  }

  // public isDayInWindow(date: dateMeta) {
  //   return (
  //     (date.month == this.taskSupplierScheduleStore.windowStart.getMonth() ||
  //       date.month == this.taskSupplierScheduleStore.windowEnd.getMonth()) &&
  //     date.day >= this.taskSupplierScheduleStore.windowStart.getDate() &&
  //     date.day <= this.taskSupplierScheduleStore.windowEnd.getDate()
  //   );
  // }
  //
  // public isHourInWindow(hour: number): boolean {
  //   if (
  //     this.taskSupplierScheduleStore.scheduledTimeDay <
  //     this.taskSupplierScheduleStore.windowEnd.getDate()
  //   ) {
  //     return true;
  //   }
  //   if (
  //     this.taskSupplierScheduleStore.scheduledTimeDay >
  //     this.taskSupplierScheduleStore.windowEnd.getDate()
  //   ) {
  //     return false;
  //   }
  //   return hour < this.taskSupplierScheduleStore.windowEnd.getHours();
  // }
  debug() {
    console.log(this.taskSupplierScheduleStore.scheduledTimeHour);
    console.log(this.taskSupplierScheduleStore.scheduledTime);
    console.log(this.processedHourBandOptions);
    console.log(this.taskSupplierScheduleStore.timeBandSelectOptions);
  }
}
